import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * fetchStockWithFallback
 * ----------------------
 * Two-stage stock-detail fetch designed to keep the Stock Modal snappy
 * even when Yahoo or Massive is slow:
 *
 *   1. Fire the **fresh enriched fetch** to `/api/stocks/{ticker}`.
 *      This is the canonical source — Yahoo + Massive merged, full
 *      profile/earnings/holdings, performance series, etc.
 *
 *   2. Start a **timeout** (default 3.5 s). If the fresh fetch hasn't
 *      resolved by then AND we haven't already shown anything to the
 *      user, fire a parallel `/api/stocks/{ticker}?stale_ok=true` call.
 *      That endpoint returns whatever's cached *right now* with no
 *      live network refresh, so it almost always returns in <500 ms.
 *
 *   3. Whichever resolves first populates the modal. The slow fresh
 *      call keeps running in the background — when it eventually
 *      lands, callers can use the second `onFresh` callback to merge
 *      the newer data in.
 *
 * The Stock Metrics tab also has a self-healing 2-second poll that
 * fills in any individual fields that arrive after the modal is
 * already mounted, so even if neither path returns a complete row at
 * first, the user sees the modal almost instantly and missing data
 * trickles in within 6–8 seconds.
 *
 * Usage:
 *   const stock = await fetchStockWithFallback(ticker, {
 *     timeoutMs: 3500,
 *     onFresh: (fresh) => setSelectedStock(fresh),  // optional 2nd update
 *   });
 *   setSelectedStock(stock);
 *
 * Returns the first usable response (fresh OR stale). Throws if BOTH
 * paths fail.
 */
export async function fetchStockWithFallback(ticker, opts = {}) {
  const { timeoutMs = 3500, onFresh = null } = opts;
  const url = `${API}/stocks/${ticker}`;
  const staleUrl = `${url}?stale_ok=true`;

  let resolved = false;
  let resolvedSource = null; // 'fresh' | 'stale'

  // Fire fresh fetch immediately. Generous 90s axios timeout so the
  // background continuation can still finish (Yahoo cold-pulls
  // sometimes take 30-60s).
  const freshPromise = axios.get(url, { withCredentials: true, timeout: 90_000 })
    .then((res) => res.data);

  // Schedule the stale fallback to fire after timeoutMs ONLY if fresh
  // hasn't returned yet. Use a sentinel so we don't fire it if fresh
  // beats the clock.
  const staleFallback = new Promise((resolve, reject) => {
    const timer = setTimeout(async () => {
      if (resolved) return resolve(null);
      try {
        const { data } = await axios.get(staleUrl, { withCredentials: true, timeout: 8_000 });
        resolve(data);
      } catch (err) {
        reject(err);
      }
    }, timeoutMs);
    // If fresh resolves first, kill the timer to skip the stale call.
    freshPromise.then(() => clearTimeout(timer)).catch(() => {});
  });

  // Race: whichever returns first populates the modal.
  // Wrap in a try/catch so a 404-on-both-fresh-and-stale path (i.e. the
  // ticker isn't in our dividend universe) doesn't bubble up — we want
  // to fall through to the `/preview` last-resort fetch instead.
  let winner = null;
  try {
    winner = await Promise.race([
      freshPromise.then((d) => { if (!resolved) { resolved = true; resolvedSource = 'fresh'; } return d; }),
      staleFallback.then((d) => {
        if (!d) return null;
        if (!resolved) { resolved = true; resolvedSource = 'stale'; }
        return d;
      }),
    ]);
  } catch {
    // Either fresh or stale rejected before the other resolved.
    // Wait a moment for the other one in case it succeeds, then
    // fall through to /preview if both ended up failing.
    winner = await freshPromise.catch(() => null);
    if (winner) { resolved = true; resolvedSource = 'fresh'; }
  }

  // If stale won the race, keep listening for the fresh response so
  // the caller can merge in the newer/richer data when it arrives.
  // Failure is non-fatal — the StockMetricsTab polling loop will fill
  // any remaining gaps.
  if (resolvedSource === 'stale' && onFresh) {
    freshPromise
      .then((fresh) => { if (fresh) onFresh(fresh); })
      .catch(() => {});
  }

  if (!winner) {
    // Round 84k Phase E3.2 (May 2026) — if BOTH the fresh + stale calls
    // failed, the ticker is NOT in our dividend database. Try the
    // on-the-fly preview endpoint which fetches yfinance data WITHOUT
    // persisting to stock_cache. Used by peer-chip clicks for stocks
    // outside DiviDrip's 4,595-ticker dividend universe (AMZN, TSLA,
    // BRK.B, etc.) so the modal still renders for browsing.
    try {
      const { data: preview } = await axios.get(`${API}/stocks/${ticker}/preview`, {
        withCredentials: true, timeout: 30_000,
      });
      return preview;
    } catch {
      // Both real endpoints AND preview failed — fall through so the
      // caller's catch block fires with the original error.
      return await freshPromise;
    }
  }

  return winner;
}
